/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import Icon, {
  ExclamationCircleFilled,
  Loading3QuartersOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { App, message } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { useCallback, useEffect, useState } from 'react';

import PauseSvg from '@/components/Icon/svgs/audio_pause.svg';
import PlaySvg from '@/components/Icon/svgs/audio_play.svg';
import ChatEmptySvg from '@/components/Icon/svgs/chat-empty.svg';
import CloseSvg from '@/components/Icon/svgs/close.svg';
import DeleteSvg from '@/components/Icon/svgs/delete.svg';
import DoneIcon from '@/components/Icon/svgs/todo_done.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
// import ShortVoiceWaveSvg from '@/components/Icon/svgs/short_voice_wave.svg';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { PROCESS_ENUM, deleteNote, relearnNote } from '@/service/note';
import type { INote, INote_List } from '@/service/note';
import useAudio from '@/ui/components/memory-detail/components/attachments/components/AudioPreview/useAudio';
import SimplyMD from '@/ui/components/memory-detail/components/attachments/components/SimplyMD';
import { generateTimeString } from '@/utils/timestamp-format';

import TopicSelect from '../components/TopicSelect';
import NoteSettings from './NoteSettings';
import NoteTip from './NoteTip';
import styles from './index.less';
import MDStyles from './markdown.less';

dayjs.extend(durationPlugin);

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  detail: INote | INote_List;
  showAttachNumber?: number;
  origin?: 'list' | 'chat' | 'todoCanvas' | 'recall' | 'select' | 'selected' | 'homepage'; // list: 笔记列表，chat: 聊天中出现, todoCanvas: todo中的source, recall: 召回，select: 选择，selected: 选中的展示
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  deleteCb?: () => void;
  cancelSelectCb?: () => void;
  operateCb?: () => void;
  showTime?: boolean;
  highlight?: string | undefined;
  noteClassName?: string;
  unread?: boolean;
  selected?: boolean;
}

function ShortVoiceNote(props: IProps): JSX.Element | null {
  const {
    className,
    detail,
    origin = 'list',
    showTime = true,
    onClick: handleClick,
    highlight,
    deleteCb,
    cancelSelectCb,
    noteClassName,
    unread = false,
    selected = false,
    ...others
  } = props;

  const { attachments = [], createTime, createTimestamp } = detail;

  const _createTimestamp = createTime || createTimestamp;

  const showCreateTime = showTime && _createTimestamp;
  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);

  const [process, setProcess] = useState<PROCESS_ENUM>();
  const [processLoading, setProcessLoading] = useState<boolean>(false);
  const { modal } = App.useApp();

  const { shadowMindId } = useGenius();

  const { t } = useTranslation();

  const item = attachments[0]; // 进入ShortVoiceNote的一定是有的

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  useEffect(() => {
    if (origin === 'list') {
      const _detail = detail as INote_List;

      setProcess(_detail.process);
    }
  }, [detail, origin]);

  const {
    isPlaying,
    handlePlayStatusChange,
    duration: _duration
  } = useAudio({
    link: item?.metadata?.audio?.link || ''
  });

  const renderDurationString = (duration: number) => {
    if (duration >= 60) {
      return dayjs.duration((duration || 0) * 1000).format('m\'s"');
    }

    return dayjs.duration((duration || 0) * 1000).format('s"');
  };

  const handleRelearn = useCallback(() => {
    const _detail = detail as INote_List;

    if (!_detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    setProcessLoading(true);
    relearnNote({ noteId: _detail.noteId, mindId: (_detail.mindId || shadowMindId)! })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || 'Failed to relearn.');
        }

        message.success('Relearning...');
      })
      .catch((err) => {
        if (err.name !== 'AxiosError') message.error(err.message);

        setProcessLoading(false);
      });
  }, [detail, shadowMindId]);

  const { run: runDelete } = useRequest(deleteNote, {
    manual: true,
    onSuccess: (res) => {
      if (res.data.code === 0) {
        message.success(t('common.deleteSuccess'));
        deleteCb?.();
      } else {
        message.error(res.data.message || t('common.deleteFail'));
      }
    },
    onError: (err) => {
      message.error(err.message || t('common.deleteFail'));
    }
  });

  return (
    <div className={classNames('flex flex-col gap-1', className)}>
      {showCreateTime ? (
        <div className="text-xs leading-5 text-[#b8ad9b]">
          {generateTimeString(_createTimestamp)}
        </div>
      ) : null}

      <div
        className={classNames(styles.container, noteClassName, 'group', {
          [styles.todoCanvas]: origin === 'todoCanvas'
        })}
        onClick={
          origin === 'todoCanvas' || process === PROCESS_ENUM.doing ? undefined : handleClick
        }
        {...others}
      >
        {process === PROCESS_ENUM.undone ? (
          <div
            className={classNames(
              styles.topContainer,
              'box-content h-6 -mx-5 px-5 pb-3 border-0 border-b border-solid border-primary-white'
            )}
          >
            <ExclamationCircleFilled
              className="text-[#FF7875] rounded-[50%] text-[18px] mr-3 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <div className={styles.warningTitle}>{'Saved but not connected.'}</div>

            <div
              className=" text-primary-white rounded-[8px] text-[12px] leading-5 bg-[#FF7875] h-7 px-3 py-1 cursor-pointer space-x-1 transition-colors duration-300 hover:bg-[rgba(255,120,117,0.8)]"
              onClick={(e) => {
                e.stopPropagation();
                handleRelearn();
              }}
            >
              {processLoading ? <Loading3QuartersOutlined spin={true} /> : <ReloadOutlined />}
              <span>{processLoading ? 'Retrying' : 'Retry'}</span>
            </div>

            {origin === 'list' && (
              <Icon
                className={
                  'w-6 h-6 p-[5px] ml-3 text-primary-black text-[12px] border border-solid border-[#eee9e4] shadow-[0_4px_12px_0_rgba(194,158,107,0.08)] bg-primary-white rounded-[30px]'
                }
                component={DeleteSvg}
                onClick={(e) => {
                  e.stopPropagation();
                  modal.confirm({
                    title: t('common.sureToDelete'),
                    onOk: () => {
                      detail.noteId && ((detail as INote_List).mindId || shadowMindId)
                        ? runDelete({
                            noteId: detail.noteId,
                            mindId: ((detail as INote_List).mindId || shadowMindId)!
                          })
                        : message.error(t('error.oops'));
                    },
                    okText: t('common.delete'),
                    cancelText: t('common.cancel')
                  });
                }}
              />
            )}
          </div>
        ) : (
          <div
            className={classNames(
              'box-content h-6 -mx-5 px-5 flex flex-nowrap gap-2 items-center',
              origin === 'todoCanvas' ? 'pb-0' : ''
            )}
          >
            <Icon
              className="w-5 h-5 bg-primary-black text-[14px] p-[3px] text-primary-white rounded-[50%] box-border"
              component={isPlaying ? PauseSvg : PlaySvg}
              onClick={(e) => {
                e.stopPropagation();
                handlePlayStatusChange();
              }}
            />
            {/* <ShortVoiceWaveSvg className={styles.waveSvg} /> */}
            <div className="text-xs leading-5 text-primary-black">
              {renderDurationString(item?.metadata?.audio?.duration || _duration)}
            </div>
            {origin === 'list' && (
              <>
                {unread && (
                  <NoteTip
                    className={classNames(
                      'absolute top-[14px] ',
                      process === PROCESS_ENUM.doing || inMobile
                        ? 'ltr:right-[50px] rtl:left-[50px]'
                        : 'ltr:right-5 rtl:left-5 group-hover:ltr:right-[50px] group-hover:rtl:left-[50px]'
                    )}
                    isLoading={process === PROCESS_ENUM.doing ? true : false}
                  />
                )}
                {process === PROCESS_ENUM.doing ? (
                  <Icon
                    className={
                      'w-6 h-6 p-[5px] ml-3 text-primary-black text-[12px] border border-solid border-[#eee9e4] absolute ltr:right-5 rtl:left-5 top-[14px] shadow-[0_4px_12px_0_rgba(194,158,107,0.08)] bg-primary-white rounded-[30px]'
                    }
                    component={DeleteSvg}
                    onClick={(e) => {
                      e.stopPropagation();
                      modal.confirm({
                        title: t('common.sureToDelete'),
                        onOk: () => {
                          detail.noteId && ((detail as INote_List).mindId || shadowMindId)
                            ? runDelete({
                                noteId: detail.noteId,
                                mindId: ((detail as INote_List).mindId || shadowMindId)!
                              })
                            : message.error(t('error.oops'));
                        },
                        okText: t('common.delete'),
                        cancelText: t('common.cancel')
                      });
                    }}
                  />
                ) : (
                  <NoteSettings
                    className={classNames(
                      'absolute ltr:right-5 rtl:left-5 top-[14px] z-10 transition-all duration-200 invisible opacity-0 ',
                      inMobile
                        ? '!visible !opacity-100'
                        : 'group-hover:visible group-hover:opacity-100'
                    )}
                    detail={detail}
                    mindId={shadowMindId}
                    onDeleteCb={deleteCb}
                  />
                )}
              </>
            )}
          </div>
        )}

        {origin === 'select' && detail.noteId && (
          <div className={styles.iconWrapper}>
            <Icon
              className={classNames(styles.icon, { [styles.done]: selected })}
              component={selected ? DoneIcon : UndoneIcon}
            />
          </div>
        )}

        {origin === 'selected' && detail.noteId && (
          <div
            className={styles.closeBtn}
            onClick={(e) => {
              e.stopPropagation();
              cancelSelectCb?.();
            }}
          >
            <Icon className={styles.icon} component={CloseSvg} />
          </div>
        )}

        <div className={classNames(styles.shortVoiceContent)}>
          <SimplyMD
            className={MDStyles.markdown}
            content={highlight ? highlight : detail.content || ''}
          />
          {/* {attachments[0].content} */}
        </div>

        {origin === 'list' && detail.noteId && (
          <div className="flex items-center">
            <TopicSelect
              sourceId={Number(detail.noteId)}
              topics={(detail as INote_List).smartTopics}
            />
            {(detail as INote_List).chatCount ? (
              <div className="ml-auto flex items-center gap-[2px] text-[#756C5D]">
                <Icon className="text-base" component={ChatEmptySvg} />
                <span className="text-xs leading-5">{(detail as INote_List).chatCount}</span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShortVoiceNote;
